import { counterService } from '../../../services/counterService'
import {
  COMPUTED_OPERATION_TYPES,
  COMPUTED_RESERVED_KEYS,
  FIELD_TYPES,
} from '../../../utility/constants'
import { getCurrentFinancialYear } from '../../../utility/utilityFunctions'
import { CURRENCY_ERROR } from './currency'
import { getDefaultValueForField } from './fieldUtil'
import { getFieldId, getLegacySubfieldId, getSelectableTableId } from './utils'

export const DROPDOWN_DEFAULT_VALUE = -1

export const autoComputeFieldValue = async (
  allFields = [],
  allValues,
  listDataMetadata,
  otherData,
  computeFields = []
) => {
  // declare and initialize empty object of computed fields allComputedFieldValues

  let allComputedFieldValues = {}

  // find all computed field
  let allComputedFields
  if (computeFields.length > 0) {
    allComputedFields = computeFields.filter((it) => {
      if (it.is_subfield) {
        return !!otherData.tableRow && it.is_computed
      }
      return it.is_computed
    })
  } else {
    allComputedFields = allFields.filter((it) => {
      if (it.is_subfield) {
        return !!otherData.tableRow && it.is_computed
      }
      return it.is_computed
    })
  }

  // loop through each computed fields
  for (const field of allComputedFields.filter(
    (it) => it.metadata.operation !== COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS
  )) {
    // get metadata

    const fieldMetadata = field.metadata

    const altComputationFieldTemplateId = fieldMetadata?.alt_field_template_id

    const altComputationListDataIds = fieldMetadata?.alt?.reduce((acc, curr) => {
      return acc.concat(curr.list_data_ids)
    }, [])
    let fieldMetaDataParams = fieldMetadata?.params
    let fieldMetaDataOperation = fieldMetadata?.operation

    if (altComputationFieldTemplateId != null && altComputationListDataIds != null) {
      const altComputationFieldValue = allValues[getFieldId(altComputationFieldTemplateId)]
      if (
        altComputationFieldValue &&
        altComputationListDataIds.includes(altComputationFieldValue)
      ) {
        fieldMetadata.alt.forEach((altObj) => {
          if (altObj.list_data_ids.includes(altComputationFieldValue)) {
            fieldMetaDataParams = altObj?.params || []
            fieldMetaDataOperation = altObj?.operation_alt
          }
        })
      }
    }

    let computedValue = getDefaultComputeFieldValue(fieldMetaDataOperation)

    for (const param of fieldMetaDataParams) {
      let computeValue = getDefaultValueForField(field)

      if (
        fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN ||
        fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN ||
        fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN
      ) {
        computeValue = getParamComputeValueTableColumn(
          param,
          fieldMetaDataOperation,
          fieldMetadata,
          allFields,
          allValues,
          listDataMetadata,
          otherData
        )
      } else if (
        fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM ||
        fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY ||
        fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT
      ) {
        computeValue = await getParamComputeValue(
          param,
          fieldMetaDataOperation,
          field,
          allFields,
          allValues,
          listDataMetadata,
          otherData
        )
      } 
      // else if (
      //   fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS &&
      //   otherData?.allProcessData
      // ) {
      //   const { computedValue: calculated } = computeFieldAcrossProcess(
      //     field,
      //     fieldMetadata?.params,
      //     fieldMetadata?.pivotFieldTemplateId,
      //     allValues[getFieldId(fieldMetadata?.pivotFieldTemplateId)],
      //     fieldMetadata?.exclusions,
      //     fieldMetadata?.operation,
      //     otherData?.tableRow,
      //     otherData?.allProcessData,
      //     otherData?.processReferenceNum
      //   )
      //   computedValue = calculated
      // }

      // update final computed value for field
      if (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
        computedValue = computeValue
      } else {
        if (
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN
        ) {
          computedValue = computedValue * computeValue
        } else {
          if (
            (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT ||
              fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN) &&
            (computeValue == undefined || computeValue == 'undefined')
          ) {
            computedValue = computedValue + ''
          } else {
            computedValue = computedValue + computeValue
          }
        }
      }
    }

    if (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
      if (otherData?.allProcessData) {
        allComputedFieldValues = {
          ...allComputedFieldValues,
          [getFieldId(field.id)]: computedValue,
        }
      }
    } else {
      allComputedFieldValues = {
        ...allComputedFieldValues,
        [getFieldId(field.id)]: computedValue,
      }
    }
  }

  return allComputedFieldValues
}

const getParamComputeValueTableColumn = (
  param,
  operation,
  fieldMetadata,
  allFields,
  allValues,
  listDataMetadata,
  otherData
) => {
  let computeValue = 0
  if (param.startsWith('^^')) {
    let fieldId
    let plusOrMinusOne = 1
    // const key = param.substring(2).split(".")[1];

    if (
      operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
      operation === COMPUTED_OPERATION_TYPES.SUM
    ) {
      fieldId = param.substring(3).split('.')[0]
      plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
    } else {
      fieldId = param.substring(2).split('.')[0]
    }

    const tableFieldTemplateId = otherData?.tableSubfieldsMap?.find(
      (it) => it.subfield_template_id == fieldId
    )?.table_field_template_id

    const tableField = allFields.find((it) => it.id == tableFieldTemplateId)

    if (tableField?.is_selectable_table) {
      for (const [key, value] of Object.entries(allValues)) {
        if (
          key.includes(`f_${fieldId}__`) &&
          otherData?.selectedTableData[getSelectableTableId(tableFieldTemplateId)]?.includes(
            key.split('__')[1]
          )
        ) {
          // incorporate mathematical operator as per config and return
          // TODO: implementation of COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN
          if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
            computeValue += plusOrMinusOne * value
          } else {
            computeValue += value // simply return for concat
          }
        }
      }
    } else {
      for (const [key, value] of Object.entries(allValues)) {
        if (key.includes(`f_${fieldId}__`)) {
          // incorporate mathematical operator as per config and return
          // TODO: implementation of COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN
          if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
            computeValue += plusOrMinusOne * value
          } else {
            computeValue += value // simply return for concat
          }
        }
      }
    }
  } else {
    console.log('Error: missing implementation of param type: ' + param)
  }
  return computeValue
}

const getParamComputeValue = async (
  param,
  operation,
  field,
  allFields,
  allValues,
  listDataMetadata,
  otherData
) => {
  try {
    if (param?.startsWith('^^')) {
      /* this section reference values from other fields */
      let fieldId
      let plusOrMinusOne = 1
      const key = param.substring(2).split('.')[1]

      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM
      ) {
        fieldId = param.substring(3).split('.')[0]
        plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
      } else {
        fieldId = param.substring(2).split('.')[0]
      }

      const field = allFields.find((it) => it.id == fieldId)

      let fieldValue = getFieldValue(key, fieldId, field, allValues, listDataMetadata)

      // incorporate mathematical operator as per config and return
      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM ||
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN ||
        operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN
      ) {
        return plusOrMinusOne * fieldValue
      } else {
        return fieldValue // simply return for concat
      }
    } else if (param?.startsWith('**')) {
      /* this section have some fixed value (processReferenceNum, currentFinancialYear) and some generated value 
      which is not directly referencing to any other fields based on some custom business logic (autoIncrementCounter) */
      const paramValue = await getParamValue(param, allValues, field, otherData)
      return paramValue
    } else {
      /* this section param itself is value - so simply return param */
      return param
    }
  } catch (error) {
    return param
  }
}

const getFieldValue = (key, fieldId, field, allValue, listDataMetadata) => {
  // find actual value of field
  // TODO: implementation for list multi ds type
  if (!allValue) {
    return ''
  }
  if (field?.data_structure === FIELD_TYPES.LIST) {
    const listItemId = allValue[getFieldId(fieldId)]
    if (listItemId == DROPDOWN_DEFAULT_VALUE) {
      return ''
    } else {
      const listDataItem = listDataMetadata?.find((it) => it.list_data_id == listItemId)

      /* if it is value get value, else get properties from  metadata*/
      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        return listDataItem?.list_data_value
      } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
        return listDataItem?.list_data_id
      } else {
        return listDataItem?.list_data_metadata[key]
      }
    }
  } else {
    let returnValue = allValue[getFieldId(fieldId)]

    if (
      !returnValue &&
      [FIELD_TYPES.CURRENCY, FIELD_TYPES.NUMBER].includes(field?.data_structure)
    ) {
      returnValue = 0
    }
    return returnValue
  }
}

const getParamValue = async (param, allValues, field, otherData) => {
  let computedValue = ''
  const key = param.substring(2)
  if (key === COMPUTED_RESERVED_KEYS.PROCESS_PREFIX) {
    computedValue = otherData?.processPrefix
  } else if (key === COMPUTED_RESERVED_KEYS.PROCESS_REFERENCE_NUM) {
    computedValue = otherData?.processReferenceNum
  } else if (key === COMPUTED_RESERVED_KEYS.CURRENT_FINANCIAL_YEAR) {
    computedValue = getCurrentFinancialYear()
  } else if (key === COMPUTED_RESERVED_KEYS.VERSION) {
    computedValue = otherData?.version
  } else if (key === COMPUTED_RESERVED_KEYS.COUNTER) {
    if (otherData.user) {
      const counterRes = await counterService.getCount(
        otherData.user,
        allValues[getFieldId(field.metadata.counterFieldTemplateId)],
        allValues[getFieldId(field.metadata.pivotFieldTemplateId)],
        field.id,
        otherData.processTemplateId,
        otherData.processReferenceNum
      )
      computedValue = counterRes?.data?.counter
    }
  } else if (key === COMPUTED_RESERVED_KEYS.SL_NO) {
    computedValue = otherData?.slNo
  }
  return computedValue
}

const getDefaultComputeFieldValue = (operation) => {
  switch (operation) {
    case COMPUTED_OPERATION_TYPES.SUM:
    case COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN:
      return 0
    case COMPUTED_OPERATION_TYPES.MULTIPLY:
    case COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN:
      return 1
    case COMPUTED_OPERATION_TYPES.CONCAT:
    case COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN:
      return ''
    default:
      return ''
  }
}

// export const computeFieldAcrossProcess = (
//   field,
//   params,
//   pivotFieldTemplateId,
//   pivotFieldListId,
//   exclusions,
//   operation,
//   rowNumber = null,
//   allProcessData = null,
//   processReferenceNum = null,
//   ndc
// ) => {
//   let computedValue = null
//   let initValue = ''
//   if (operation === COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
//     initValue = 0
//   }

//   let pivotFieldListIdAcrossProcess = pivotFieldListId

//   if (!pivotFieldListIdAcrossProcess) {
//     pivotFieldListIdAcrossProcess = allProcessData?.find(
//       (it) => it.process_reference_num == processReferenceNum
//     )?.data['f_' + pivotFieldTemplateId]

//     if (!pivotFieldListIdAcrossProcess) {
//       return { computedValue }
//     }
//   }

//   const processIdCurrencyMap = {}
//   // here, we are actually calculating the sum across process
//   allProcessData?.forEach((datum) => {
//     const pd = datum.data
//     if (pd[getFieldId(pivotFieldTemplateId)] == pivotFieldListIdAcrossProcess) {
//       let flag = true
//       for (let value of exclusions) {
//         if (value.list_data_id == pd[getFieldId(value.field_template_id)]) {
//           flag = false
//           break
//         }
//       }
//       if (flag) {
//         // get currency of sum across field
//         if (ndc) {
//           const ndcRow = ndc?.filter(
//             (it) =>
//               it.process_reference_num === datum.process_reference_num &&
//               it.field_template_id === getParamId(params[0], field)
//           )
//           processIdCurrencyMap[datum.process_reference_num] = ndcRow[0]?.currency_code
//         }

//         params.forEach((param) => {
//           if (param.startsWith('^^')) {
//             let fieldId = param.substring(2).split('.')[0]
//             fieldId = rowNumber ? fieldId + '__' + rowNumber : fieldId
//             if (Object.keys(pd).includes(getFieldId(fieldId))) {
//               computedValue += pd[getFieldId(fieldId)]
//             }
//           }
//         })
//       }
//     }
//   })
//   return { computedValue, ndcUpdated: processIdCurrencyMap }
// }

export const getParamId = (param, field) => {
  const isPolarityAppended = [
    COMPUTED_OPERATION_TYPES.SUM,
    COMPUTED_OPERATION_TYPES.MULTIPLY,
  ].includes(field?.metadata?.operation)

  return isPolarityAppended
    ? +param.substring(3, param.indexOf('.'))
    : +param.substring(2, param.indexOf('.'))
}

// export const computeFieldSumAcrossProcessOnInitialLoad = (
//   fields = [],
//   allValues = {},
//   allProcessData,
//   processReferenceNum,
//   ndc,
//   defaultCurrency
// ) => {
//   // get all computed fields
//   let allSumAcrossComputationFields = fields?.filter((it) => {
//     return it?.is_computed && it?.metadata?.operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS
//   })

//   let allComputedSumAcrossValues = {}
//   let nonDefaultCurrencyUpdated = {}

//   // loop through each computed fields
//   for (let field of allSumAcrossComputationFields) {
//     // get metadata
//     const fieldMetadata = field?.metadata

//     const pivotFieldTemplateId = fieldMetadata?.pivotFieldTemplateId

//     const exclusions = fieldMetadata?.exclusions || []

//     const operation = fieldMetadata?.operation

//     if (field.is_subfield) {
//       const rows = Object.keys(allValues)
//         .filter((key) => key.includes(`f_${field.id}__`))
//         .map((key) => {
//           return key.split('__')[1]
//         })

//       for (let row of rows) {
//         // compute across process

//         const { computedValue, ndcUpdated = {} } = computeFieldAcrossProcess(
//           field,
//           fieldMetadata?.params,
//           pivotFieldTemplateId,
//           allValues[getFieldId(pivotFieldTemplateId)],
//           exclusions,
//           operation,
//           row,
//           allProcessData,
//           processReferenceNum,
//           ndc
//         )

//         let value = computedValue
//         if (!computedValue) {
//           value = getDefaultValueForField(field)
//         }

//         allComputedSumAcrossValues[getLegacySubfieldId(field.id, row)] = value

//         // calculate currency
//         const curArray = Object.values(ndcUpdated).map((it) => (it ? it : defaultCurrency))
//         const isCurrencySame = curArray.every((it) => it === curArray[0])
//         nonDefaultCurrencyUpdated[field.id] = isCurrencySame ? curArray[0] : CURRENCY_ERROR
//       }
//     } else {
//       // compute across process
//       const { computedValue, ndcUpdated = {} } = computeFieldAcrossProcess(
//         field,
//         fieldMetadata?.params,
//         pivotFieldTemplateId,
//         allValues[getFieldId(pivotFieldTemplateId)],
//         exclusions,
//         operation,
//         null,
//         allProcessData,
//         processReferenceNum,
//         ndc
//       )

//       let value = computedValue
//       if (!computedValue) {
//         value = getDefaultValueForField(field)
//       }

//       allComputedSumAcrossValues[getFieldId(field.id)] = value

//       // calculate currency
//       const curArray = Object.values(ndcUpdated).map((it) => (it ? it : defaultCurrency))
//       const isCurrencySame = curArray.every((it) => it === curArray[0])
//       nonDefaultCurrencyUpdated[field.id] = isCurrencySame ? curArray[0] : CURRENCY_ERROR
//     }
//   }

//   return { allComputedSumAcrossValues, nonDefaultCurrencyUpdated }
// }
